import React, { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { titleCase } from "@/lib/format";
import { PageHeader, DataTable } from "@/components/shared";
import { Card } from "@/components/agent/AgentPanels";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Trophy, Save, BellRing } from "lucide-react";
import { DatePicker, DateTimePicker } from "@/components/DatePicker";

function TargetCell({ r, canEdit, reload }) {
  const [v, setV] = useState(r.daily_call_target);
  useEffect(() => { setV(r.daily_call_target); }, [r.daily_call_target]);
  if (!canEdit) return <span>{r.daily_call_target}</span>;
  const save = async () => {
    try { await api.put(`/users/${r.user_id}/call-target`, { target: Number(v) }); toast.success(`Target set to ${v}`); reload(); } catch (ex) { toast.error(apiError(ex.response?.data?.detail)); }
  };
  return (
    <div className="flex gap-1 items-center">
      <Input type="number" className="h-8 w-20" value={v} onChange={(e) => setV(e.target.value)} data-testid="target-input" />
      {Number(v) !== r.daily_call_target && <Button size="icon" variant="ghost" className="h-8 w-8" onClick={save} data-testid="target-save-btn"><Save className="h-4 w-4" /></Button>}
    </div>
  );
}

function SlaSettings({ onChange }) {
  const [s, setS] = useState(null);
  const [def, setDef] = useState("");
  useEffect(() => {
    api.get("/settings/sla").then(({ data }) => setS(data));
    api.get("/settings/call-target-default").then(({ data }) => setDef(String(data.target))).catch(() => {});
  }, []);
  if (!s) return null;
  const save = async () => {
    try { await api.put("/settings/sla", s); toast.success("Alert rules saved"); } catch (ex) { toast.error(apiError(ex.response?.data?.detail)); }
  };
  const runNow = async () => {
    try { await api.post("/alerts/run-now"); toast.success("Check started. New alerts appear in the bell shortly."); } catch (ex) { toast.error(apiError(ex.response?.data?.detail)); }
  };
  const saveDef = async () => {
    try { await api.put("/settings/call-target-default", { target: Number(def) }); toast.success("Default target saved"); onChange(); } catch (ex) { toast.error(apiError(ex.response?.data?.detail)); }
  };
  return (
    <Card title="Missed follow-up alerts & targets" actions={<>
      <Button size="sm" variant="outline" onClick={runNow} data-testid="sla-run-now-btn"><BellRing className="h-4 w-4 mr-1" />Check now</Button>
      <Button size="sm" onClick={save} data-testid="sla-save-btn"><Save className="h-4 w-4 mr-1" />Save</Button>
    </>}>
      <div className="flex flex-wrap gap-5 text-sm items-center">
        <Label className="flex items-center gap-2">New lead not called within <Input type="number" className="w-20 h-8" value={s.new_lead_minutes} onChange={(e) => setS({ ...s, new_lead_minutes: e.target.value })} data-testid="sla-new-input" /> min</Label>
        <Label className="flex items-center gap-2">Follow-up overdue by <Input type="number" className="w-20 h-8" value={s.followup_overdue_minutes} onChange={(e) => setS({ ...s, followup_overdue_minutes: e.target.value })} data-testid="sla-fu-input" /> min</Label>
        <Label className="flex items-center gap-2"><Switch checked={s.email} onCheckedChange={(c) => setS({ ...s, email: c })} data-testid="sla-email-switch" />Also email team leaders</Label>
        <Label className="flex items-center gap-2">Default daily call target <Input type="number" className="w-20 h-8" placeholder="100" value={def} onChange={(e) => setDef(e.target.value)} data-testid="default-target-input" />
          <Button size="sm" variant="outline" className="h-8" onClick={saveDef} disabled={!def} data-testid="default-target-save">Set</Button></Label>
      </div>
      <p className="text-xs text-muted-foreground mt-2">Checked automatically every 15 minutes. Alerts go to the caller's team leader (or admins if the caller has no team leader). Each lead is alerted once per missed step.</p>
    </Card>
  );
}

export default function TeamPerformance() {
  const { hasPerm } = useAuth();
  const today = new Date(Date.now() + 330 * 60000).toISOString().slice(0, 10);
  const [from, setFrom] = useState(today.slice(0, 8) + "01");
  const [to, setTo] = useState(today);
  const [data, setData] = useState(null);
  const canEdit = hasPerm("scoring:manage");
  const load = useCallback(() => api.get("/reports/team-performance", { params: { date_from: from, date_to: to } }).then(({ data }) => setData(data)), [from, to]);
  useEffect(() => { load(); }, [load]);
  const cols = [
    { key: "name", header: "Caller", render: (r) => <div><div className="font-semibold">{r.name}</div><div className="text-[11px] text-muted-foreground">{r.employee_id} · {titleCase(r.role)}{r.team_leader ? ` · TL ${r.team_leader}` : ""}</div></div> },
    { key: "leads_assigned", header: "Assigned" },
    { key: "calls", header: "Calls", render: (r) => <span className="font-semibold" data-testid="perf-calls">{r.calls}</span> },
    { key: "connect_rate", header: "Connect rate", render: (r) => `${r.connect_rate}% (${r.connected})` },
    { key: "follow_ups_done", header: "Follow-ups done" },
    { key: "avg_first_response_min", header: "1st response", render: (r) => (r.avg_first_response_min == null ? "—" : `${r.avg_first_response_min} min`) },
    { key: "qualified", header: "Qualified" },
    { key: "conv", header: "Conversions", render: (r) => <span>{r.insurance_converted + r.loan_submitted}<div className="text-[11px] text-muted-foreground">{r.conversion_rate}% · ins {r.insurance_converted} · loan {r.loan_submitted}</div></span> },
    { key: "overdue_follow_ups", header: "Overdue now", render: (r) => <span className={r.overdue_follow_ups ? "text-rose-600 font-semibold" : ""}>{r.overdue_follow_ups}</span> },
    { key: "daily_call_target", header: "Daily target", render: (r) => <TargetCell r={r} canEdit={canEdit} reload={load} /> },
  ];
  const teamCols = [
    { key: "team", header: "Team", render: (t) => <span className="font-semibold">{t.team}</span> },
    { key: "members", header: "People" }, { key: "calls", header: "Calls" },
    { key: "connect_rate", header: "Connect rate", render: (t) => `${t.connect_rate}%` },
    { key: "follow_ups_done", header: "Follow-ups" }, { key: "leads_assigned", header: "Assigned" },
    { key: "conversions", header: "Conversions" }, { key: "overdue_follow_ups", header: "Overdue" },
  ];
  return (
    <div className="space-y-4">
      <PageHeader title="Team Performance" subtitle="Calls, connect rate, follow-ups and conversions from real call logs" icon={Trophy} />
      <div className="flex flex-wrap gap-3 items-end">
        <div><div className="text-xs text-muted-foreground mb-1">From</div><DatePicker value={from} onChange={setFrom} className="w-48" testId="perf-from" /></div>
        <div><div className="text-xs text-muted-foreground mb-1">To</div><DatePicker value={to} onChange={setTo} className="w-48" testId="perf-to" /></div>
      </div>
      {canEdit && <SlaSettings onChange={load} />}
      {data?.teams?.length > 1 && <Card title="By team"><DataTable columns={teamCols} rows={data.teams} rowTestId="perf-team-row" /></Card>}
      <DataTable columns={cols} rows={data?.people || []} loading={!data} rowTestId="perf-person-row" emptyText="No callers in scope" />
      {data && <p className="text-xs text-muted-foreground">{data.note}</p>}
    </div>
  );
}
